import { Controller } from '@hotwired/stimulus'

// Moves the list cover up or down in its banner by dragging it. The position (0 top,
// 1 bottom) goes to a hidden field; left empty, the server keeps the framing it read.
export default class extends Controller {
  static targets = ['image', 'field', 'surface']
  static values = { initial: { type: Number, default: 0.5 } }

  // Touch listeners that may cancel the gesture (not passive): otherwise the page scrolls,
  // or the native modal follows the finger down.
  surfaceTargetConnected(surface) {
    surface.addEventListener('touchstart', this.#holdTouch, { passive: false })
    surface.addEventListener('touchmove', this.#holdTouch, { passive: false })
  }

  surfaceTargetDisconnected(surface) {
    surface.removeEventListener('touchstart', this.#holdTouch)
    surface.removeEventListener('touchmove', this.#holdTouch)
  }

  start() {
    this.element.dataset.repositioning = ''
  }

  done() {
    delete this.element.dataset.repositioning
  }

  // New photo or none: back to the framing the server will read.
  reset() {
    this.fieldTarget.value = ''
    this.initialValue = 0.5
    this.imageTarget.style.objectPosition = ''
    this.done()
  }

  grab(event) {
    // No text selection nor native image drag, which swallow the release of the button.
    event.preventDefault()
    // Only the first finger moves the photo: a second one would restart the drag from elsewhere.
    if (this.#drag) return

    this.#drag = { id: event.pointerId, y: event.clientY, position: this.#position(), overflow: this.#overflow() }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  move(event) {
    if (!this.#drag || event.pointerId !== this.#drag.id || this.#drag.overflow <= 0) return
    if (event.pointerType === 'mouse' && event.buttons === 0) return this.release()

    const { y, position, overflow } = this.#drag
    const next = Math.min(1, Math.max(0, position - (event.clientY - y) / overflow))
    this.imageTarget.style.objectPosition = `50% ${(next * 100).toFixed(1)}%`
    this.fieldTarget.value = next.toFixed(3)
  }

  release(event) {
    if (this.#drag && event?.pointerId !== undefined && event.pointerId !== this.#drag.id) return

    this.#drag = null
  }

  // Swallows the click that ends a drag (see the view: it would reopen the photo picker).
  noop() {}

  #drag = null

  #holdTouch = (event) => event.preventDefault()

  #position() {
    return this.fieldTarget.value === '' ? this.initialValue : parseFloat(this.fieldTarget.value)
  }

  // Height of the photo hidden by the banner, in pixels on screen.
  #overflow() {
    const { naturalWidth, naturalHeight } = this.imageTarget
    const box = this.imageTarget.getBoundingClientRect()
    if (!naturalWidth) return 0

    return (box.width * naturalHeight) / naturalWidth - box.height
  }
}
